import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, KeyRound, Lock, User, X } from 'lucide-react';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { loginTenantAdminSession } from '../../lib/api.js';
import { isEditorLauncherVisibleInBrowser } from '../../lib/editor-gate.js';

/**
 * Übersetzt Fehler der Anmeldung in klare deutsche Hinweise.
 * Gleiche Fehlerfälle wie im Shop-Verwaltungs-Login (AdminDashboardView).
 */
export function editorLoginErrorMessage(err) {
  const msg = String(err?.message || '');
  if (err?.status === 401 || msg === 'invalid_credentials' || msg.includes('401')) {
    return 'Anmeldung fehlgeschlagen. Bitte Benutzername und Passwort prüfen.';
  }
  if (err?.status === 503 || msg === 'auth_not_configured' || msg.includes('503')) {
    return 'Der Anmeldedienst ist auf dem Server nicht konfiguriert.';
  }
  if (msg.includes('Failed to fetch') || msg.includes('Network')) {
    return 'Keine Verbindung zum Anmeldedienst. Bitte später erneut versuchen.';
  }
  return 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
}

/**
 * EditorLoginLauncher — sichtbarer Einstieg in den Storefront-Editor.
 *
 * Besucher sehen unten links den Button „Editor-Anmeldung“. Er öffnet einen
 * Dialog mit Benutzername und Passwort (Rolle tenant_admin). Nach erfolgreicher
 * Anmeldung schaltet der Editor direkt in den Bearbeiten-Modus. Sobald eine
 * Editor-Sitzung besteht (canEdit), verschwindet der Button; die EditToolbar
 * übernimmt dann.
 */
export function EditorLoginLauncher() {
  const { canEdit, enterEditMode, recheckSession } = useEditMode();
  const [requested, setRequested] = useState(() => isEditorLauncherVisibleInBrowser());
  const [open, setOpen] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const baseId = useId();
  const loginId = `${baseId}-login`;
  const passwordId = `${baseId}-password`;
  const firstFieldRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setPassword('');
    setError('');
  }, []);

  useEffect(() => {
    const onHash = () => setRequested(isEditorLauncherVisibleInBrowser());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, close]);

  useEffect(() => {
    if (open) firstFieldRef.current?.focus();
  }, [open]);

  // Nur bei gezielter Adresse (#editor-login) oder Sitzungs-Marker sichtbar; sonst gar kein Element.
  if (canEdit || !requested) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedLogin = login.trim();
    if (!trimmedLogin || !password) {
      setError('Bitte Benutzername und Passwort eingeben.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await loginTenantAdminSession(trimmedLogin, password);
      setPassword('');
      enterEditMode();
      recheckSession();
      setOpen(false);
    } catch (err) {
      setError(editorLoginErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="editor-login-launcher"
        className="fixed bottom-4 left-4 z-[60] flex cursor-pointer items-center gap-2 rounded-full bg-[var(--primary)] px-4 py-2.5 text-sm font-bold text-[var(--primary-foreground,#ffffff)] shadow-xl hover:opacity-90"
      >
        <Lock className="h-4 w-4" aria-hidden="true" />
        Editor-Anmeldung
      </button>

      {open && (
        <div
          data-testid="editor-login-overlay"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => {
            if (!busy) close();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${baseId}-title`}
            data-testid="editor-login-dialog"
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id={`${baseId}-title`} className="text-lg font-bold text-slate-900">
                  Editor-Anmeldung
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Melden Sie sich an, um Texte und Inhalte direkt im Shop zu bearbeiten.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={busy}
                aria-label="Schließen"
                className="cursor-pointer rounded-full p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
              {error && (
                <div
                  role="alert"
                  data-testid="editor-login-error"
                  className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor={loginId} className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
                  Benutzername
                </label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    id={loginId}
                    ref={firstFieldRef}
                    type="text"
                    autoComplete="username"
                    value={login}
                    onChange={(e) => {
                      setLogin(e.target.value);
                      if (error) setError('');
                    }}
                    aria-required="true"
                    className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor={passwordId} className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
                  Passwort
                </label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    id={passwordId}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                    }}
                    aria-required="true"
                    className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="editor-login-submit"
                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-2.5 text-sm font-bold text-[var(--primary-foreground,#ffffff)] hover:opacity-90 disabled:opacity-60"
              >
                {busy ? 'Wird angemeldet …' : 'Anmelden'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
